<template>
  <div v-if="slug && detail" class="product-detail">
    <!-- 顶部介绍：单独一块，宽度与首页区块一致 -->
    <section class="pd-intro-wrap">
      <article class="pd-intro-card block block--intro">
        <div class="block__copy">
          <div class="block__title-row">
            <img v-if="productItem.icon" :src="productItem.icon" alt="" class="block__icon" />
            <h1 class="block__title block__title--hero">{{ displayName }}</h1>
          </div>
          <p class="block__subtitle">{{ heroSubtitle }}</p>
          <p class="block__overview">{{ detail.overview }}</p>
          <div class="block__cta">
            <button type="button" class="cta cta--button" @click="openDemoModal">
              {{ $t("trial") }}
              <span class="cta__chev" aria-hidden="true">&gt;</span>
            </button>
          </div>
        </div>
        <div class="pd-intro-mock-wrap">
          <product-showcase-mock :image="heroImage" :alt-text="displayName" />
        </div>
      </article>
    </section>

    <!--
      导航与模块必须同属一块「长」父级，sticky 才在整个模块区滚动时生效；
      若父级只有导航条高度，下滑后整条父级移出视口，吸顶会失效。
    -->
    <div ref="stickyScope" class="pd-sticky-scope">
      <div class="module-nav-bleed">
        <div
          v-if="moduleNavCoversHeader"
          class="module-nav__spacer"
          :style="{ height: moduleNavBarHeight + 'px' }"
          aria-hidden="true"
        />
        <nav
          ref="moduleNav"
          class="module-nav"
          :class="{ 'module-nav--covers-header': moduleNavCoversHeader }"
          aria-label="核心模块"
        >
          <div class="module-nav__inner">
            <button
              v-for="(mod, idx) in detail.modules"
              :key="idx"
              type="button"
              class="module-nav__btn"
              :class="{ 'module-nav__btn--active': activeModuleIndex === idx }"
              @click="scrollToModule(idx)"
            >
              {{ mod.title }}
            </button>
          </div>
        </nav>
      </div>

      <section class="pd-modules section-core">
      <article
        v-for="(mod, idx) in detail.modules"
        :id="moduleAnchorId(idx)"
        :key="'m-' + idx"
        v-reveal
        class="pd-module feature-block"
        :class="'reveal--delay-' + ((idx % 3) + 1)"
      >
        <a-row type="flex" align="middle" :gutter="[28, 36]">
          <a-col :xs="24" :md="12" :order="idx % 2 === 0 ? 1 : 2">
            <div class="feature-copy pd-module-copy">
              <h2 class="feature-title">{{ mod.title }}</h2>
              <p v-if="pointBody(idx)" class="module-point-desc">{{ pointBody(idx) }}</p>
              <p v-for="(para, pi) in mod.paragraphs" :key="pi" class="feature-detail pd-module-detail">
                {{ para }}
              </p>
            </div>
          </a-col>
          <a-col :xs="24" :md="12" :order="idx % 2 === 0 ? 2 : 1">
            <div class="feature-image-wrap lift-hover pd-module-visual">
              <product-showcase-mock :image="moduleImage(idx)" :alt-text="mod.title" />
            </div>
          </a-col>
        </a-row>
      </article>
      </section>
    </div>

    <section v-if="productPricing" v-reveal class="pricing-sheet detail-pricing">
      <h2 class="section-title">{{ productPricing.title }}</h2>
      <p class="section-lead">{{ productPricing.subtitle }}</p>

      <div class="plan-grid">
        <article
          v-for="(plan, index) in productPricing.plans"
          :key="plan.id"
          class="plan-card"
          :class="{ 'plan-card--highlight': plan.highlight }"
          :style="{ transitionDelay: `${index * 60}ms` }"
        >
          <header class="plan-head" :class="{ 'plan-head--highlight': plan.highlight }">
            <p class="plan-name">{{ plan.name }}</p>
            <p class="plan-subtitle">{{ plan.subtitle }}</p>
            <span v-if="plan.badge" class="plan-badge">{{ plan.badge }}</span>
          </header>

          <div class="plan-body">
            <p class="plan-price">
              <strong>{{ plan.price }}</strong>
              <span>/年</span>
            </p>

            <a-button
              class="plan-cta"
              :type="plan.ctaType"
              :ghost="plan.ctaType === 'default'"
              block
              @click="onPlanCta(plan)"
            >
              {{ plan.ctaText }}
            </a-button>

            <ul class="plan-features">
              <li v-for="feature in plan.features" :key="feature">
                <a-icon type="check" class="feature-icon" />
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>
        </article>
      </div>

      <div class="addon-wrap">
        <article v-for="addon in productPricing.addons" :key="addon.id" class="addon-item">
          <p class="addon-name">{{ addon.name }}</p>
          <p class="addon-price">{{ addon.price }}<span>/年</span></p>
        </article>
      </div>

      <div class="benefit-wrap">
        <p v-for="benefit in productPricing.benefits" :key="benefit" class="benefit-item">
          <a-icon type="check" />
          <span>{{ benefit }}</span>
        </p>
      </div>

      <p class="section-note">{{ productPricing.note }}</p>
    </section>
    <demo-register-modal :visible="demoModalVisible" @update:visible="demoModalVisible = $event" />
  </div>
</template>

<script>
import { PRODUCT_SLUGS, productDetailContent } from "@/mock/defaultData";
import { pointsFor, subtitleFor } from "@/apps/official/utils/productCopy";
import ProductShowcaseMock from "../components/ProductShowcaseMock.vue";
import DemoRegisterModal from "@/apps/official/components/DemoRegisterModal.vue";
import omsHomeImg from "@/static/oms/home.png";
import omsModule1 from "@/static/oms/1.png";
import omsModule2 from "@/static/oms/2.png";
import omsModule3 from "@/static/oms/3.png";
import omsModule4 from "@/static/oms/4.png";
import wmsHomeImg from "@/static/wms/home.png";
import wmsModule1 from "@/static/wms/1.png";
import wmsModule2 from "@/static/wms/2.png";
import wmsModule3 from "@/static/wms/3.png";
import wmsModule4 from "@/static/wms/4.png";
import hwcHomeImg from "@/static/hwc/home.png";
import hwcModule1 from "@/static/hwc/1.png";
import hwcModule2 from "@/static/hwc/2.png";
import hwcModule3 from "@/static/hwc/3.png";
import hwcModule4 from "@/static/hwc/4.png";
import drpHomeImg from "@/static/drp/home.png";
import drpModule1 from "@/static/drp/1.png";
import drpModule2 from "@/static/drp/2.png";
import drpModule3 from "@/static/drp/3.png";
import drpModule4 from "@/static/drp/4.png";
import biHomeImg from "@/static/bi/home.png";
import biModule1 from "@/static/bi/1.png";
import biModule2 from "@/static/bi/2.png";
import biModule3 from "@/static/bi/3.png";
import biModule4 from "@/static/bi/4.png";
import erpHomeImg from "@/static/erp/home.png";
import erpModule1 from "@/static/erp/1.png";
import erpModule2 from "@/static/erp/2.png";
import erpModule3 from "@/static/erp/3.png";
import erpModule4 from "@/static/erp/4.png";
import pingceHomeImg from "@/static/pingce/home.png";
import pingceModule1 from "@/static/pingce/1.png";
import pingceModule2 from "@/static/pingce/2.png";
import pingceModule3 from "@/static/pingce/3.png";
import pingceModule4 from "@/static/pingce/4.png";
import chatHomeImg from "@/static/chat/home.png";
import chatModule1 from "@/static/chat/1.png";
import chatModule2 from "@/static/chat/2.png";
import chatModule3 from "@/static/chat/3.png";
import chatModule4 from "@/static/chat/4.png";
import tmsHomeImg from "@/static/tms/home.png";
import tmsModule1 from "@/static/tms/1.png";
import tmsModule2 from "@/static/tms/2.png";
import tmsModule3 from "@/static/tms/3.png";
import tmsModule4 from "@/static/tms/4.png";
import suyuanHomeImg from "@/static/suyuan/home.png";
import suyuanModule1 from "@/static/suyuan/1.png";
import suyuanModule2 from "@/static/suyuan/2.png";
import suyuanModule3 from "@/static/suyuan/3.png";
import suyuanModule4 from "@/static/suyuan/4.png";

const PRODUCT_LOCAL_IMAGE_MAP = {
  oms: {
    home: omsHomeImg,
    modules: [omsModule1, omsModule2, omsModule3, omsModule4]
  },
  wms: {
    home: wmsHomeImg,
    modules: [wmsModule1, wmsModule2, wmsModule3, wmsModule4]
  },
  hwc: {
    home: hwcHomeImg,
    modules: [hwcModule1, hwcModule2, hwcModule3, hwcModule4]
  },
  erp: {
    home: erpHomeImg,
    modules: [erpModule1, erpModule2, erpModule3, erpModule4]
  },
  drp: {
    home: drpHomeImg,
    modules: [drpModule1, drpModule2, drpModule3, drpModule4]
  },
  bi: {
    home: biHomeImg,
    modules: [biModule1, biModule2, biModule3, biModule4]
  },
  pingce: {
    home: pingceHomeImg,
    modules: [pingceModule1, pingceModule2, pingceModule3, pingceModule4]
  },
  chat: {
    home: chatHomeImg,
    modules: [chatModule1, chatModule2, chatModule3, chatModule4]
  },
  tms: {
    home: tmsHomeImg,
    modules: [tmsModule1, tmsModule2, tmsModule3, tmsModule4]
  },
  suyuan: {
    home: suyuanHomeImg,
    modules: [suyuanModule1, suyuanModule2, suyuanModule3, suyuanModule4]
  }
};

const HEADER_OFFSET = 72;
const HEADER_OFFSET_MOBILE = 72;
const NAV_HEIGHT = 56;
const SHARED_PRICING_BLOCK = {
  plans: [
    {
      id: "basic",
      name: "基础版",
      subtitle: "初创海外仓首选",
      price: "¥0",
      ctaText: "免费使用",
      ctaType: "default",
      highlight: false,
      badge: "",
      features: [
        "每月 10,000 单",
        "2 个仓库/代理仓",
        "3 个员工账号",
        "仓库基础报表",
        "成本测算自动计算",
        "物流轨迹自动监控",
        "7x24 小时在线客服"
      ]
    },
    {
      id: "standard",
      name: "标准版",
      subtitle: "仓储效率更进一步",
      price: "¥28,000",
      ctaText: "预约演示",
      ctaType: "primary",
      highlight: false,
      badge: "",
      features: [
        "包含基础版能力",
        "5 个仓库",
        "不限代理仓数量",
        "不限员工账号",
        "不限订单数量",
        "业务时效预警",
        "线上&线下实施服务"
      ]
    },
    {
      id: "professional",
      name: "专业版",
      subtitle: "经营能力全面提升",
      price: "¥48,000",
      ctaText: "预约演示",
      ctaType: "primary",
      highlight: true,
      badge: "最受欢迎",
      features: [
        "包含标准版能力",
        "10 个仓库",
        "序列号管理",
        "AI 智能打包",
        "自动补货建议",
        "高级异常管理"
      ]
    },
    {
      id: "flagship",
      name: "旗舰版",
      subtitle: "卓越海外仓的最佳选择",
      price: "¥68,000",
      ctaText: "预约演示",
      ctaType: "primary",
      highlight: true,
      badge: "",
      features: [
        "包含专业版能力",
        "20 个仓库",
        "高级品销管理",
        "智能设备对接",
        "全渠道订单聚合",
        "企业级私有数据库"
      ]
    }
  ],
  addons: [
    { id: "seat", name: "增加 1 个仓库", price: "¥5,000" },
    { id: "ai", name: "AI 智能打包", price: "¥5,000" }
  ],
  benefits: ["免费对接打单系统", "免费对接代理仓", "免费自定义面单水印", "免费批量拆分导入面单"],
  note: "* 增值方案不适用于基础版"
};

const PRODUCT_PRICING = {
  oms: {
    title: "OMS 价格方案",
    subtitle: "按订单量与自动化深度阶梯计费，履约规则可随业务升级平滑扩展，保护前期投入。"
  },
  wms: {
    title: "WMS 价格方案",
    subtitle: "按仓点数与作业复杂度组合定价，从单仓试跑到多仓组网均可按需扩容。"
  },
  hwc: {
    title: "海外仓管理系统价格方案",
    subtitle: "按货主数量、仓点数与费用配置深度阶梯计费，工作台、WMS 与费用设置模块随业务规模弹性扩展。"
  },
  erp: {
    title: "ERP 价格方案",
    subtitle: "按店铺数与 SKU 量灵活扩展，小团队起步够用，做大了再解锁进阶成本分析与多仓协同。"
  },
  drp: {
    title: "DRP 价格方案",
    subtitle: "按渠道层级、经销商数量与协同范围计费，价盘与分货能力随分销网络同步放大。"
  },
  bi: {
    title: "BI 价格方案",
    subtitle: "按店铺数量与看板深度灵活加购，小团队起步无压力，做大了再解锁高阶分析。"
  },
  tms: {
    title: "TMS 价格方案",
    subtitle: "按运单量、承运商数量与调度复杂度阶梯计费，智能排线与在途监控能力随业务规模弹性扩展。"
  },
  suyuan: {
    title: "溯源系统价格方案",
    subtitle: "按赋码量、追溯节点数与行业合规深度组合定价，防伪验真与召回管理模块可按品牌规模加购。"
  },
  pingce: {
    title: "评测系统价格方案",
    subtitle: "按店铺数、任务量与刷手席位阶梯计费，财务对账与数据统计模块可按业务规模弹性加购。"
  },
  chat: {
    title: "聊天系统价格方案",
    subtitle: "按坐席数与会话量组合定价，智能客服与跨境翻译可按峰值流量弹性加购。"
  }
};

export default {
  components: { ProductShowcaseMock, DemoRegisterModal },
  inject: ["officialShell"],
  data() {
    return {
      activeModuleIndex: 0,
      moduleNavCoversHeader: false,
      moduleNavBarHeight: 64,
      demoModalVisible: false
    };
  },
  computed: {
    slug() {
      const s = (this.$route.params.slug || "").toLowerCase();
      return PRODUCT_SLUGS.includes(s) ? s : null;
    },
    detail() {
      if (!this.slug) return null;
      return productDetailContent[this.slug] || null;
    },
    product() {
      const id = `prd-${this.slug}`;
      const list = (this.officialShell.ctx && this.officialShell.ctx.products) || [];
      return list.find((p) => p.id === id) || null;
    },
    productItem() {
      return this.product || { name: this.detail.title, subtitle: "", intro: "", icon: "", image: "" };
    },
    displayName() {
      if (this.product && this.product.name) return this.product.name;
      return this.detail ? this.detail.title : "";
    },
    heroSubtitle() {
      return subtitleFor(this.productItem);
    },
    heroPoints() {
      return pointsFor(this.productItem);
    },
    heroImage() {
      const localHome = this.slug ? PRODUCT_LOCAL_IMAGE_MAP[this.slug]?.home : "";
      return localHome || this.productItem.image || "";
    },
    productPricing() {
      if (!this.slug) return null;
      const meta = PRODUCT_PRICING[this.slug];
      return meta ? { ...meta, ...SHARED_PRICING_BLOCK } : null;
    }
  },
  watch: {
    $route: "onRouteChange"
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize, { passive: true });
    this.$nextTick(() => {
      this.measureModuleNavBar();
      this.onScroll();
      this.scrollHashIfAny();
    });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    headerOffsetPx() {
      return window.matchMedia("(max-width: 991px)").matches ? HEADER_OFFSET_MOBILE : HEADER_OFFSET;
    },
    measureModuleNavBar() {
      const el = this.$refs.moduleNav;
      if (!el) return;
      const h = el.offsetHeight;
      if (h > 0) this.moduleNavBarHeight = h;
    },
    onResize() {
      this.measureModuleNavBar();
      this.onScroll();
    },
    updateModuleNavCoverState() {
      const scope = this.$refs.stickyScope;
      if (!scope) return;
      const rect = scope.getBoundingClientRect();
      const navH = this.moduleNavBarHeight || 0;
      const next = rect.top <= 0 && rect.bottom > navH;
      if (next !== this.moduleNavCoversHeader) {
        this.moduleNavCoversHeader = next;
        this.$nextTick(() => this.measureModuleNavBar());
      }
    },
    /** 当前用于锚点/高亮判断的「内容区顶线」（模块导航底边 + 间距） */
    moduleAnchorLinePx() {
      const el = this.$refs.moduleNav;
      if (!el) return this.scrollAnchorOffset();
      return el.getBoundingClientRect().bottom + 8;
    },
    pointAt(idx) {
      const pts = this.heroPoints;
      if (!pts || !pts[idx]) return null;
      const p = pts[idx];
      const title = (p.title && String(p.title).trim()) || "";
      const body = (p.body && String(p.body).trim()) || "";
      if (!title && !body) return null;
      return { title, body };
    },
    /** 模块标题下展示的要点说明（仅 body，不用 feature-subtitle） */
    pointBody(idx) {
      const p = this.pointAt(idx);
      return p && p.body ? p.body : "";
    },
    moduleAnchorId(idx) {
      return `mod-${idx}`;
    },
    moduleImage(idx) {
      if (!this.slug) return this.productItem.image || "";
      const localModules = PRODUCT_LOCAL_IMAGE_MAP[this.slug]?.modules || [];
      return localModules[idx] || this.heroImage;
    },
    scrollAnchorOffset() {
      return this.headerOffsetPx() + NAV_HEIGHT + 10;
    },
    scrollToModule(idx) {
      const id = this.moduleAnchorId(idx);
      const el = document.getElementById(id);
      if (!el) return;
      const line = this.moduleAnchorLinePx();
      const y = el.getBoundingClientRect().top + window.scrollY - line;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    },
    onScroll() {
      if (!this.detail || !this.detail.modules.length) return;
      this.updateModuleNavCoverState();
      const line = this.moduleAnchorLinePx();
      let best = 0;
      for (let i = 0; i < this.detail.modules.length; i++) {
        const el = document.getElementById(this.moduleAnchorId(i));
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= line) best = i;
      }
      this.activeModuleIndex = best;
    },
    scrollHashIfAny() {
      const hash = this.$route.hash && this.$route.hash.replace(/^#/, "");
      if (!hash || !/^mod-\d+$/.test(hash)) return;
      const idx = parseInt(hash.replace("mod-", ""), 10);
      if (Number.isNaN(idx)) return;
      this.$nextTick(() => this.scrollToModule(idx));
    },
    onRouteChange() {
      this.$nextTick(() => this.scrollHashIfAny());
    },
    openDemoModal() {
      this.demoModalVisible = true;
    },
    onPlanCta(plan) {
      if (!plan || plan.ctaText !== "预约演示") return;
      this.openDemoModal();
    }
  }
};
</script>

<style scoped>
.product-detail {
  width: 100%;
  padding: 0 0 40px;
}

/* 包住「模块导航 + 模块列表」，sticky 的约束盒才覆盖整段滚动区域 */
.pd-sticky-scope {
  position: relative;
}

/* 与 CoreFeaturesSection .section 同宽 */
.pd-intro-wrap {
  width: 90%;
  max-width: none;
  margin: 20px auto 0;
  padding: 0 24px;
  box-sizing: border-box;
}

.pd-intro-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 40px 48px;
  align-items: start;
  margin: 0;
  padding: 44px clamp(24px, 4vw, 52px) 48px;
}

.block--intro {
  border-bottom: none;
}

.pd-intro-mock-wrap {
  width: 100%;
  max-width: min(560px, 100%);
  margin-left: auto;
  margin-right: auto;
}

.pd-intro-mock-wrap >>> .mock {
  border: none;
  box-shadow: none;
}

.block__copy {
  max-width: 36rem;
  min-width: 0;
  padding: 6px 0 8px;
  margin: auto;
}

.block__title-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}

.block__icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.block__title {
  margin: 0;
  font-weight: 700;
  color: var(--primary-dark);
  line-height: 1.22;
  letter-spacing: -0.02em;
}

.block__title--hero {
  font-size: clamp(28px, 3.2vw, 36px);
}

.block__subtitle {
  margin: 0 0 14px;
  font-size: 15px;
  line-height: 1.7;
  color: #1677ff;
  font-weight: 600;
}

.block__overview {
  margin: 0 0 28px;
  font-size: 15px;
  line-height: 1.85;
  color: #3a4a66;
}

.block__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cta {
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 26px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  background: linear-gradient(180deg, var(--primary-light) 0%, var(--primary) 100%);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.28);
  transition:
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.28s ease,
    filter 0.22s ease;
}

.cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 34px rgba(37, 99, 235, 0.38);
  filter: brightness(1.02);
}

.cta--button {
  appearance: none;
}

.cta__chev {
  font-weight: 400;
  opacity: 0.9;
}

/* 全宽导航条 */
.module-nav-bleed {
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  margin-top: 28px;
  margin-bottom: 8px;
}

.module-nav {
  position: -webkit-sticky;
  position: sticky;
  top: var(--header-h);
  z-index: 16;
  width: 100%;
  padding: 14px clamp(16px, 4vw, 48px);
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(246, 249, 255, 0.97) 100%
  );
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(224, 233, 255, 0.95);
  border-bottom: 1px solid rgba(224, 233, 255, 0.95);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
}

/* 模块区已进入视口深处：吸顶并盖住官网顶栏 */
.module-nav.module-nav--covers-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1010;
  margin-left: 0;
  margin-right: 0;
}

.module-nav__inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  justify-content: center;
}

.module-nav__btn {
  appearance: none;
  border: 1px solid rgba(59, 130, 246, 0.32);
  background: rgba(255, 255, 255, 0.95);
  color: #1e3a5f;
  font-size: 14px;
  line-height: 1.35;
  padding: 10px 18px;
  border-radius: 999px;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.module-nav__btn:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.module-nav__btn--active {
  background: linear-gradient(180deg, var(--primary-light) 0%, var(--primary) 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.35);
}

/* 模块区：对齐 CoreFeaturesSection */
.section-core {
  max-width: 1200px;
  margin: 28px auto 0;
  padding: 40px 24px 8px;
  box-sizing: border-box;
}

.feature-block {
  padding: 72px 0;
  border-bottom: 1px solid var(--border);
}

.feature-block:last-child {
  border-bottom: 0;
  padding-bottom: 48px;
}

.feature-copy {
  padding: 12px 8px;
  text-align: center;
}

.pd-module-copy {
  min-width: 320px;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.feature-title {
  margin: 0 0 12px;
  font-size: 28px;
  line-height: 1.3;
  color: var(--primary-dark);
  font-weight: 700;
}

.module-point-desc {
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.8;
  color: #64748b;
  text-align: center;
}

.feature-detail {
  margin: 0 0 14px;
  font-size: 15px;
  line-height: 1.85;
  color: #3a4a66;
  text-align: center;
}

.pd-module-detail:last-of-type {
  margin-bottom: 0;
}

.pd-module-visual {
  width: 100%;
  max-width: min(420px, 76%);
  margin-left: auto;
  margin-right: auto;
}

.pd-module-visual >>> .block__visual {
  max-width: 100%;
}

.pd-module-visual >>> .mock__stage {
  aspect-ratio: 16 / 10;
}

.pd-module {
  scroll-margin-top: 152px;
}

.pricing-sheet {
  width: 80%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 40px 34px 28px;
  border-radius: 20px;
  background:
    radial-gradient(circle at 100% 0%, rgba(87, 124, 255, 0.2) 0%, rgba(87, 124, 255, 0) 40%),
    linear-gradient(180deg, #f8faff 0%, #ecf2ff 100%);
  box-shadow:
    0 20px 40px rgba(14, 48, 118, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.detail-pricing {
  margin-top: 24px;
}

.section-title {
  margin: 0;
  text-align: center;
  font-size: 30px;
  line-height: 1.25;
  color: var(--primary-dark);
}

.section-lead {
  max-width: 760px;
  margin: 12px auto 28px;
  text-align: center;
  color: #5a6a86;
  line-height: 1.7;
  font-size: 14px;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.plan-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #d8e2ff;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 12px 26px rgba(20, 50, 115, 0.08);
  min-height: 640px;
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease,
    border-color 0.28s ease;
}

.plan-card:hover {
  transform: translateY(-6px);
  border-color: #bdd0ff;
  box-shadow: 0 18px 34px rgba(25, 66, 149, 0.16);
}

.plan-head {
  position: relative;
  min-height: 96px;
  padding: 18px 14px 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  text-align: center;
  border-bottom: 1px dashed #d5e0fb;
}

.plan-head--highlight {
  background: linear-gradient(120deg, #4f63ea 0%, #284dcf 100%);
}

.plan-name {
  margin: 0;
  font-size: 31px;
  line-height: 1.1;
  color: #132867;
  font-weight: 700;
}

.plan-head--highlight .plan-name,
.plan-head--highlight .plan-subtitle {
  color: #fff;
}

.plan-subtitle {
  margin: 4px 0 0;
  color: #6e7a99;
  font-size: 12px;
}

.plan-badge {
  position: absolute;
  right: 8px;
  top: 8px;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.2;
  color: #7a4600;
  background: linear-gradient(90deg, #ffe29f 0%, #ffc65e 100%);
  box-shadow: 0 4px 9px rgba(163, 97, 0, 0.2);
}

.plan-body {
  padding: 16px 18px 18px;
}

.plan-price {
  margin: 0;
  text-align: center;
  color: #29344f;
}

.plan-price strong {
  font-size: 35px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.plan-price span {
  margin-left: 2px;
  font-size: 14px;
  color: #5c6683;
}

.plan-cta {
  margin-top: 12px;
  border-radius: 999px;
  height: 34px;
  font-size: 13px;
  font-weight: 600;
}

.plan-features {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
}

.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  color: #40506d;
  font-size: 13px;
  line-height: 1.5;
}

.feature-icon {
  margin-top: 3px;
  color: #2f6bff;
  font-size: 12px;
}

.addon-wrap {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.addon-item {
  border-radius: 12px;
  border: 1px solid #d8e2ff;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 10px 20px rgba(22, 52, 117, 0.08);
  text-align: center;
  padding: 16px;
}

.addon-name {
  margin: 0;
  color: #6a7694;
  font-size: 12px;
}

.addon-price {
  margin: 4px 0 0;
  font-size: 31px;
  font-weight: 700;
  color: #2b3654;
  line-height: 1.1;
}

.addon-price span {
  font-size: 14px;
  color: #6d7894;
  font-weight: 500;
}

.benefit-wrap {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.benefit-item {
  margin: 0;
  border-radius: 10px;
  border: 1px solid #dce5ff;
  background: #fff;
  text-align: center;
  color: #4f5b77;
  font-size: 12px;
  box-shadow: 0 8px 14px rgba(23, 52, 114, 0.06);
  padding: 12px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.benefit-item .anticon {
  color: #54b35b;
  font-size: 11px;
}

.section-note {
  margin: 8px 0 0;
  text-align: right;
  color: #8c97b2;
  font-size: 11px;
}

@media (prefers-reduced-motion: reduce) {
  .cta:hover {
    transform: none;
    filter: none;
  }
}

@media (max-width: 991px) {
  .pd-intro-wrap {
    width: calc(100% - 28px);
    margin-top: 16px;
  }

  .pd-intro-card {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 32px 24px 36px;
  }

  .pd-intro-mock-wrap {
    order: -1;
    max-width: min(480px, 96%);
  }

  .section-core {
    width: calc(100% - 28px);
    margin-top: 20px;
    padding: 32px 28px 4px;
  }

  .pricing-sheet {
    width: calc(100% - 28px);
    max-width: none;
    padding: 24px 20px;
  }

  .detail-pricing {
    margin-top: 20px;
  }

  .section-title {
    font-size: 26px;
  }

  .plan-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .plan-card {
    min-height: 0;
  }

  .benefit-wrap {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feature-block {
    padding: 56px 0;
  }

  .feature-title {
    font-size: 22px;
  }

  .module-nav {
    top: var(--header-h);
    padding-left: 14px;
    padding-right: 14px;
  }

  .module-nav__btn {
    font-size: 12px;
    padding: 8px 14px;
  }

  .pd-module {
    scroll-margin-top: 136px;
  }

  .pd-module-visual {
    max-width: min(360px, 88%);
  }
}

@media (max-width: 575px) {
  .section-core {
    padding: 28px 20px 4px;
  }

  .pricing-sheet {
    padding: 20px 16px;
    border-radius: 16px;
  }

  .section-title {
    font-size: 22px;
  }

  .section-lead {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .plan-grid,
  .addon-wrap,
  .benefit-wrap {
    grid-template-columns: minmax(0, 1fr);
  }

  .plan-name {
    font-size: 27px;
  }

  .plan-price strong,
  .addon-price {
    font-size: 29px;
  }

  .feature-title {
    font-size: 20px;
  }
}
</style>
